import { useId } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { DeploymentServingState, ProjectDeployment } from '../../api';
import { statusFor } from '../../labels';
import { Fold, ResponsiveTable } from '../primitives/ResponsiveTable';
import type { Column } from '../primitives/ResponsiveTable';
import { Icon } from '../icons';
import { commitURL } from '../project/commitLink';
import { Initiator, OutcomeBadge, ServingLabel, WhenCell } from '../status/StatusPieces';
import { inProgress, rowState } from '../status/statusModel';
import {
  applyFilters,
  componentName,
  componentOptions,
  filtersFrom,
  hasFilters,
  outcomeFilterLabel,
  sinceFilterLabel,
  withFilters,
} from './deploymentFilters';
import type { DeploymentFilters, OutcomeFilter, SinceFilter } from './deploymentFilters';
import { Input, Select } from '../primitives';

/**
 * DeploymentHistory (CO-150): the one rendering of a deployment history —
 * the Overview's recent list (compact) and the Deployments section (full,
 * with search and filters). What it shows, in order of importance: the
 * component, the change (commit message, commit, branch), the outcome of
 * the attempt, whether its release serves now, and when it started and how
 * long it took. The server and who started it are secondary: they give way
 * on a tablet and are on the deployment's own page.
 *
 * "Succeeded" and "Serving now" are different facts: four successive
 * releases can all have succeeded while only the newest serves.
 */
export function DeploymentHistory({
  deployments,
  serving,
  variant = 'full',
  deploymentPath,
  groupPath,
  repositoryOf,
}: {
  deployments: ProjectDeployment[];
  /** Outcome and serving relation per deployment, from the server. */
  serving?: Record<string, DeploymentServingState> | null;
  variant?: 'full' | 'compact';
  deploymentPath: (deploymentId: string) => string;
  groupPath?: (groupId: string) => string;
  /** The server is on the deployment's own page (one repeated value per environment). */
  serverName?: (serverId: string) => string;
  /** The repository a deployment's commit lives in, to link the commit (CO-100). */
  repositoryOf?: (deployment: ProjectDeployment) => string | undefined;
  /** Kept for callers; the list no longer restates the environment. */
  environmentName?: string;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const full = variant === 'full';
  const filters = filtersFrom(searchParams);
  const shown = full ? applyFilters(deployments, filters, serving) : deployments;

  const columns: Column<ProjectDeployment>[] = [
    {
      id: 'component',
      header: 'Component',
      card: 'title',
      width: 16,
      cell: (deployment) => (
        <span className="cell-stack">
          <span className="cell-stack__primary">{componentName(deployment)}</span>
          {deployment.group_id && groupPath ? (
            <Link className="cell-stack__secondary" to={groupPath(deployment.group_id)}>
              Part of a coordinated deployment
            </Link>
          ) : null}
        </span>
      ),
    },
    {
      id: 'change',
      header: 'Change',
      width: 'fill',
      cell: (deployment) => <ChangeCell deployment={deployment} repository={repositoryOf?.(deployment)} />,
    },
    {
      id: 'outcome',
      header: 'Outcome',
      card: 'status',
      width: 'status',
      cell: (deployment) => {
        const state = rowState(deployment, serving);
        const db = databaseNote(deployment);
        return (
          <span className="cell-stack">
            <OutcomeBadge outcome={state.outcome} />
            {/* In a narrow table the Serving column gives way; its word stays here. */}
            <Fold>
              <ServingLabel serving={state.serving} />
            </Fold>
            {db ? <span className="cell-stack__secondary">{db}</span> : null}
          </span>
        );
      },
    },
    {
      id: 'serving',
      header: 'Serving',
      card: 'status',
      priority: 'secondary',
      width: 'status',
      cell: (deployment) => <ServingLabel serving={rowState(deployment, serving).serving} />,
    },
    {
      id: 'when',
      header: 'Started',
      cardLabel: 'Started',
      width: 'time',
      cell: (deployment) => (
        <WhenCell
          created={deployment.created_at}
          started={deployment.started_at}
          completed={deployment.completed_at}
          running={inProgress(rowState(deployment, serving).outcome)}
          waiting={rowState(deployment, serving).outcome === 'queued'}
        />
      ),
    },
    ...(full
      ? ([
          {
            id: 'by',
            header: 'Started by',
            priority: 'secondary',
            card: 'none',
            width: 12,
            cell: (deployment) => <Initiator value={deployment.created_by} />,
          },
        ] satisfies Column<ProjectDeployment>[])
      : []),
  ];

  const table = (
    <ResponsiveTable
      rows={shown}
      columns={columns}
      rowKey={(deployment) => deployment.id}
      caption="Deployments, newest first"
      testId={full ? 'deployment-history' : 'deployment-history-compact'}
      className="deployment-history"
      rowProps={(deployment) => ({ testId: `deployment-row-${deployment.id}` })}
      rowLink={(deployment) => ({
        to: withQuery(deploymentPath(deployment.id), full ? withFilters(new URLSearchParams(), filters).toString() : ''),
        accessibleName: `View the ${shaOf(deployment).slice(0, 7)} deployment of ${componentName(deployment)}`,
      })}
    />
  );

  if (!full) return table;

  return (
    <div className="history" data-testid="deployments-history">
      <HistoryFilters
        filters={filters}
        components={componentOptions(deployments)}
        onChange={(next) => setSearchParams(withFilters(searchParams, next), { replace: true })}
      />
      {hasFilters(filters) ? (
        <p
          className="history__scope"
          role="status"
          aria-live="polite"
          aria-label={`${shown.length} of ${deployments.length} deployments match the filters`}
          data-testid="history-scope"
        >
          {shown.length}/{deployments.length} deployments
        </p>
      ) : null}
      {shown.length === 0 ? (
        <p className="quiet-empty" data-testid="history-no-match">
          No matches
        </p>
      ) : (
        table
      )}
    </div>
  );
}

/** Carries the filters onto the detail link, so going back keeps them. */
function withQuery(path: string, query: string): string {
  if (!query) return path;
  return `${path}${path.includes('?') ? '&' : '?'}${query}`;
}

function shaOf(deployment: ProjectDeployment): string {
  return deployment.scope === 'frontend_only' && deployment.frontend_commit_sha ? deployment.frontend_commit_sha : deployment.source_commit_sha;
}

/** A database change worth a word beside the outcome; "no change" says nothing. */
function databaseNote(deployment: ProjectDeployment): string | null {
  const value = deployment.db_change_classification;
  if (!value || value === 'no_db_change' || (value as string) === 'unknown') return null;
  if (rowState(deployment).outcome === 'rehearsal') return null;
  return statusFor('dbChange', value).label;
}

function ChangeCell({ deployment, repository }: { deployment: ProjectDeployment; repository?: string }) {
  const sha = shaOf(deployment);
  const message = (deployment.scope === 'frontend_only' ? deployment.frontend_commit_message || deployment.source_commit_message : deployment.source_commit_message)?.split('\n')[0];
  const href = commitURL(repository, sha);
  const branch = deployment.scope === 'frontend_only' ? deployment.frontend_commit_ref || deployment.source_branch : deployment.source_branch;
  const commit = href ? (
    <a href={href} target="_blank" rel="noreferrer">
      <code translate="no">{sha.slice(0, 7)}</code>
      <span className="visually-hidden"> (opens the repository in a new tab)</span>
    </a>
  ) : (
    <code translate="no">{sha.slice(0, 7)}</code>
  );
  // The message is read from the repository (CO-152). While it is not
  // known the commit itself names the change; nothing claims there is none.
  if (!message) {
    return (
      <span className="cell-stack">
        <span className="cell-stack__primary">
          Commit {commit}
          {branch ? <> on {branch}</> : null}
        </span>
      </span>
    );
  }
  return (
    <span className="cell-stack">
      <span className="cell-stack__primary cell-clamp" title={message}>
        {message}
      </span>
      <span className="cell-stack__secondary">
        {commit}
        {branch ? <> on {branch}</> : null}
      </span>
    </span>
  );
}

function HistoryFilters({
  filters,
  components,
  onChange,
}: {
  filters: DeploymentFilters;
  components: string[];
  onChange: (next: DeploymentFilters) => void;
}) {
  const id = useId();
  return (
    <div className="history-filters" role="search" aria-label="Filter deployments">
      <label className="history-filters__search" htmlFor={`${id}-q`}>
        <span className="visually-hidden">Search deployments</span>
        <Icon name="search" size={16} />
        <Input
          id={`${id}-q`}
          type="search"
          value={filters.q}
          placeholder="Search commit, message, branch or release…"
          onChange={(event) => onChange({ ...filters, q: event.target.value })}
          autoComplete="off"
          spellCheck={false}
        />
      </label>
      <label className="history-filters__field">
        <span className="history-filters__label">Component</span>
        <Select value={filters.component} onChange={(event) => onChange({ ...filters, component: event.target.value })}>
          <option value="">All components</option>
          {components.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </Select>
      </label>
      <label className="history-filters__field">
        <span className="history-filters__label">Outcome</span>
        <Select value={filters.outcome} onChange={(event) => onChange({ ...filters, outcome: event.target.value as OutcomeFilter })}>
          {(Object.keys(outcomeFilterLabel) as OutcomeFilter[]).map((value) => (
            <option key={value} value={value}>
              {outcomeFilterLabel[value]}
            </option>
          ))}
        </Select>
      </label>
      <label className="history-filters__field">
        <span className="history-filters__label">Started</span>
        <Select value={filters.since} onChange={(event) => onChange({ ...filters, since: event.target.value as SinceFilter })}>
          {(Object.keys(sinceFilterLabel) as SinceFilter[]).map((value) => (
            <option key={value} value={value}>
              {sinceFilterLabel[value]}
            </option>
          ))}
        </Select>
      </label>
      {hasFilters(filters) ? (
        <button type="button" className="button button--ghost button--small history-filters__clear" onClick={() => onChange({ q: '', component: '', outcome: 'all', since: 'all' })}>
          <Icon name="x-circle" size={16} />
          Clear filters
        </button>
      ) : null}
    </div>
  );
}
